import { useCallback, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOemPersona } from './OemPersonaContext';
import { routeForView } from './oem-routes';
import { TOUR, type TourStop } from './seed/tour';

// =========================================================================
// Guided tour engine. Fork-owned (zone C).
//
// The demo kept the tour in two globals (`TOUR_AT` and the bar's `.on`
// class) and ran every stop through `eval`. Here the state is one small
// external store, `{ active, index }`, mirrored to sessionStorage so a reload
// mid-tour keeps the bar and the stop. It is scoped to the tab: a new tab
// starts without a tour, which is what a presenter opening a second window
// expects.
//
// A store rather than a context, so nothing has to be mounted above the
// three places that drive it (the tour bar, Help → Guided tour and the
// account menu). Each caller's `useTour()` does its own navigation.
//
// Engine layer (layer 2): it mutates state and navigates. The pure route
// derivation, `tourRoute`, is exported for the Help tab's transform.
// =========================================================================

/** sessionStorage key. Namespaced alongside `oem-plus:brand` and `oem-plus:persona`. */
export const TOUR_STORAGE_KEY = 'oem-plus:tour';

export interface TourState {
  readonly active: boolean;
  /** Zero-based stop index. Kept when the tour ends so Help can offer "resume". */
  readonly index: number;
}

const INACTIVE: TourState = { active: false, index: 0 };

function clampIndex(index: number): number {
  return Math.max(0, Math.min(TOUR.length - 1, Math.trunc(index)));
}

/** Storage read that survives private mode, blocked site data and SSR. */
function readStored(): TourState {
  if (typeof window === 'undefined') return INACTIVE;
  try {
    const raw = window.sessionStorage.getItem(TOUR_STORAGE_KEY);
    if (!raw) return INACTIVE;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return INACTIVE;
    const { active, index } = parsed as Record<string, unknown>;
    if (typeof active !== 'boolean' || typeof index !== 'number' || !Number.isFinite(index)) {
      return INACTIVE;
    }
    return { active, index: clampIndex(index) };
  } catch {
    return INACTIVE;
  }
}

function writeStored(next: TourState): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private window or blocked storage. The tour still runs; it just won't
    // survive a reload.
  }
}

let current: TourState = readStored();
const listeners = new Set<() => void>();

function setTourState(next: TourState): void {
  current = next;
  writeStored(next);
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): TourState {
  return current;
}

function getServerSnapshot(): TourState {
  return INACTIVE;
}

/**
 * The route a stop navigates to. It goes through `routeForView`, the same
 * table every other ported cross-reference uses, and falls back to `/` so a
 * stop whose view loses its route still lands somewhere.
 */
export function tourRoute(stop: TourStop): string {
  return routeForView(stop.action.view, stop.action.sub) ?? '/';
}

/** Where "End tour" lands: Help → Guided tour, as in the demo. */
export const TOUR_HOME = routeForView('help', 'tour') ?? '/help';

export interface TourControls extends TourState {
  /** The stop at `index`. Defined whether or not the tour is active. */
  readonly stop: TourStop;
  readonly total: number;
  readonly isFirst: boolean;
  readonly isLast: boolean;
  /** Start from stop 1. */
  start: () => void;
  /** Jump to a stop (clamped) and start the tour if it is not running. */
  go: (index: number) => void;
  next: () => void;
  back: () => void;
  /** Hide the bar and return to Help → Guided tour. */
  end: () => void;
}

/**
 * Read and drive the guided tour.
 *
 * Navigation happens in the caller's router context. A stop that names a
 * persona switches to it with `navigate: false`, so the persona's home page
 * never races the stop's own route.
 */
export function useTour(): TourControls {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const navigate = useNavigate();
  const { personaId, setPersona } = useOemPersona();

  const go = useCallback(
    (index: number) => {
      const target = clampIndex(index);
      const stop = TOUR[target];
      if (stop.persona && stop.persona !== personaId) {
        setPersona(stop.persona, { navigate: false });
      }
      setTourState({ active: true, index: target });
      navigate(tourRoute(stop));
    },
    [navigate, personaId, setPersona],
  );

  const end = useCallback(() => {
    setTourState({ active: false, index: current.index });
    navigate(TOUR_HOME);
  }, [navigate]);

  return {
    ...state,
    stop: TOUR[state.index],
    total: TOUR.length,
    isFirst: state.index === 0,
    isLast: state.index === TOUR.length - 1,
    start: () => go(0),
    go,
    next: () => go(current.index + 1),
    back: () => go(current.index - 1),
    end,
  };
}
